
import { Options, Vue } from 'vue-class-component';
import ModalBox from '@/shared/components/modal-box/index.vue';
import { Inject, Prop, Watch } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { EventEmitter } from 'events';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import { orderBy } from 'lodash';
import store from '@/trust-atcs-app/store';
import { ComplianceForm, ExternalExaminer, IndividualPracticeRequest, PracticeAtcs } from '@/shared/modules/typescript-trustapi-client/models';
import { AuthorizedUser } from '@/trust-atcs-app/models/authorized-user';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';

@Options({
  components: {
    ModalBox,
    LoadingIndicator,
    ErrorIndicator,
  },
  mixins: [FormatDateTimeMixin]
})
export default class AppointAndTerminateEE extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Prop({ default: null })
  private practice!: ComplianceForm;

  private trustContact = null;
  private errorWithContact = false;


  private loadingData = false;
  private errorLoadingData = false;
  private loadingEvents = false;

  private submitting = false;
  private searchinguser = false;
  private gettingMoreUser = false;
  private submitPressed = false;
  private searchPressed = false;

  private selectedEE: ExternalExaminer = null as any;
  private reasonForTermination: string = null as any;
  private nameKeyword = '';
  private suburbPostcode = '';

  private externalExaminers: ExternalExaminer[] = [];
  private totalResultCount = 0;
  private numResultsShowing = 0;

  private dirtyFields: Array<boolean> = [];

  created(): void {
    this.dirtyFields = [false];
    if (this.existingEE) {
      this.getEEUser();
    }
  }

  private closeModal(): void {
    this.eventCtrl.emit('loadCardData');
    this.$emit('close');
  }

  private async getEEUser(): Promise<void> {
    try {
      this.loadingData = true
      const resp: any = await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerGetExternalExaminerByIndividualPracticeIdIndividualPracticeIdGet(
        this.practice?.practice?.externalExaminer?.id
      );
      this.selectedEE = resp?.data.data as ExternalExaminer;

    } catch {
      this.errorLoadingData = true;
    } finally {
      this.loadingData = false;
    }
  }

  private submitButtonPressed(): void {
    if (this.existingEE) {
      this.terminateEERequest();
    } else {
      this.appointmentEERequest();
    }
  }
  
  private async terminateEERequest(): Promise<void> {
    try {
      this.submitPressed = true;
      if (!this.isFormValid()) {
        throw ('Incomplete form');
      }
      this.submitting = true;
      await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerTerminateEeRequestFormPost(
        {
          externalExaminerId: this.selectedEE?.id,
          practiceCoreId: this.practice?.practice?.practiceCoreId,
          terminationReason: this.reasonForTermination
        }
      )
      this.eventCtrl.emit('show-snackbar', 'success', 'Request to terminate External Examiner has been submitted');
      this.closeModal();
    } catch (err: any) {
      if (err.response?.data?.errorCode == 90001) {
        this.eventCtrl.emit('show-snackbar', 'error', 'The External Examiner information has been updated. Please refresh the page to view the latest information.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error submitting request: ' + err);
      }
    } finally {
      this.submitting = false;
    }
  }

  private async appointmentEERequest(): Promise<void> {
    try {
      this.submitPressed = true;
      if (!this.isFormValid()) {
        throw ('Incomplete form - no External Examiner');
      }
      this.submitting = true;
      await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerAppointEeRequestFormPost(
        {
          externalExaminerId: this.selectedEE?.id,
          practiceCoreId: this.practice?.practice.practiceCoreId,
        }
      )
      this.eventCtrl.emit('show-snackbar', 'success', 'Request to appoint External Examiner has been submitted');
      this.closeModal();
    } catch (err: any) {
      if (err.response?.data?.errorCode == 90001) {
        this.eventCtrl.emit('show-snackbar', 'error', 'The External Examiner information has been updated. Please refresh the page to view the latest information.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'error', 'Error submitting request: ' + err);
      }
    } finally {
      this.submitting = false;
    }
  }

  private async searchUser(skipI = 0, takeI = 10, reset = false): Promise<void> {
    try {
      this.searchPressed = true;
      if (this.nameKeyword.trim()?.length == 0 && this.suburbPostcode.trim()?.length == 0) {
        return;
      }
      if (reset) {
        this.externalExaminers = [];
        this.totalResultCount = 0;
        this.numResultsShowing = 0;
        this.searchinguser = true;
      } else {
        this.gettingMoreUser = true;
      }

      const resp: any = await this.apiService.ExternalExaminerApi?.apiV2ExternalExaminerSearchExternalExaminersPost(
        {
          skip: skipI,
          take: takeI,
          keyword: this.nameKeyword,
          suburbPostCode: this.suburbPostcode
        }
      );

      if (reset) {
        this.externalExaminers = resp.data.data;
        this.totalResultCount = resp.data.totalCount;
        this.numResultsShowing = this.totalResultCount <= 10 ? this.totalResultCount : this.externalExaminers.length;
      } else {
        this.externalExaminers.push(...resp.data.data);
        this.numResultsShowing = this.externalExaminers.length;
      }
    
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error getting External Examiners: ' + err);
    } finally {
      this.searchinguser = false;
      this.gettingMoreUser = false;
    }
  }

  private isFormValid(): boolean {
    if (this.existingEE) {
      return this.reasonForTermination != null && this.reasonForTermination?.length > 0;
    } else {
      return !!this.selectedEE?.id
    }
    
  }

  get displayShowMoreButton() : boolean {
    return this.numResultsShowing < this.totalResultCount;
  }

  get existingEE(): boolean {
    return this.practice?.practice?.externalExaminer?.lawId ?? false;
  }

  get userAccount(): AuthorizedUser {
    return store.getters.getUserAccount;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

}

